import React, { useEffect, useRef, useState } from "react";
import { compose } from "redux";
import enhancer from "views/pages/validator";
import Api from "api/Api";
import Toaster from "components/common/Toaster";
import ReceiptPrivacyModal from "./ReceiptPrivacyModal";
import { defaultReceiptConsentMessage } from "util/receiptPageDefaults";

function ReceiptEmailCapture(props) {
  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    submitCount,
    receiptName,
    previewMode = false,
    onEmailSubmitted,
    downloadUrl,
    onDownload,
    consentMessage: consentMessageProp,
    receiptPrivacyText: privacyTextProp,
    receiptPrivacyPolicy: privacyPolicyProp,
    primaryColor,
    secondaryColor
  } = props;

  const toaster = useRef(null);
  const [loader, setLoader] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [consentMessage, setConsentMessage] = useState(
    consentMessageProp || defaultReceiptConsentMessage
  );
  const [privacyText, setPrivacyText] = useState(privacyTextProp || "");
  const [privacyPolicy, setPrivacyPolicy] = useState(privacyPolicyProp || "");

  useEffect(() => {
    if (consentMessageProp) {
      setConsentMessage(consentMessageProp);
    }
    setPrivacyText(privacyTextProp || "");
    setPrivacyPolicy(privacyPolicyProp || "");
  }, [consentMessageProp, privacyTextProp, privacyPolicyProp]);

  useEffect(() => {
    if (!receiptName || previewMode || consentMessageProp) return;
    Api.post("user/consentMessage", { receiptName })
      .then(res => {
        if (res.data.status && res.data.data) {
          if (res.data.data.consentMessage) {
            setConsentMessage(res.data.data.consentMessage);
          }
          if (res.data.data.receiptPrivacyText) {
            setPrivacyText(res.data.data.receiptPrivacyText);
          }
          if (res.data.data.receiptPrivacyPolicy) {
            setPrivacyPolicy(res.data.data.receiptPrivacyPolicy);
          }
        }
      })
      .catch(() => {});
  }, [receiptName, previewMode, consentMessageProp]);

  const showError = field =>
    (errors[field] && touched[field]) || submitCount > 0;

  const showPrivacyLink = Boolean(privacyPolicy && privacyPolicy.trim());
  const privacyLinkLabel = privacyText || "Privacy Policy";

  const onSubmit = e => {
    e.preventDefault();
    if (previewMode) return;

    const { values, isValid, handleSubmit } = props;
    if (isValid) {
      setLoader(true);
      Api.post("user/submitEmail", {
        email: values.email,
        receiptName
      })
        .then(res => {
          if (res.data.status) {
            if (toaster.current) toaster.current.success(res.data.message);
            setSubmitted(true);
            if (onEmailSubmitted) onEmailSubmitted(values.email);
          } else if (toaster.current) {
            toaster.current.error(res.data.message);
          }
          setLoader(false);
        })
        .catch(err => {
          setLoader(false);
          if (toaster.current) toaster.current.error(err.message);
        });
    }
    handleSubmit();
  };

  if (submitted) {
    return (
      <div className="rp-email-capture rp-email-capture--inline rp-email-success">
        <h2 className="rp-email-title">Receipt sent</h2>
        <p className="rp-email-success-text">
          Check your inbox for a copy of your receipt.
        </p>
        <Toaster ref={toaster} />
      </div>
    );
  }

  return (
    <div className="rp-email-capture rp-email-capture--inline">
      <h2 className="rp-email-title">Send the receipt to your email</h2>

      <form onSubmit={onSubmit}>
        <div className="rp-email-field">
          <input
            type="email"
            className="rp-email-input"
            name="email"
            id="receipt-email-inline"
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="email"
            autoComplete="email"
            disabled={previewMode}
          />
          {showError("email") && (
            <span className="rp-email-error">{errors.email}</span>
          )}
        </div>

        <label className="rp-email-consent" htmlFor="receipt-consent-inline">
          <input
            type="checkbox"
            id="receipt-consent-inline"
            name="checkbox"
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={previewMode}
          />
          <span className="rp-email-consent-text">
            {consentMessage}
            {showPrivacyLink && (
              <>
                {" "}
                <button
                  type="button"
                  className="rp-privacy-link"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPrivacyOpen(true);
                  }}
                >
                  {privacyLinkLabel}
                </button>
              </>
            )}
          </span>
        </label>
        {showError("checkbox") && (
          <span className="rp-email-error">{errors.checkbox}</span>
        )}

        <div className="rp-action-row">
          <button
            type="submit"
            className="rp-email-submit rp-action-btn"
            disabled={loader || previewMode}
          >
            {loader ? (
              <span className="rp-email-spinner" aria-label="Sending" />
            ) : (
              "Send email"
            )}
          </button>
          {downloadUrl && (
            <a
              className="rp-btn rp-btn-outline rp-action-btn"
              href={downloadUrl}
              onClick={e => {
                if (previewMode) e.preventDefault();
                if (onDownload) onDownload(e);
              }}
            >
              <span className="rp-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    d="M12 4v10M8 10l4 4 4-4M5 20h14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="rp-btn-label">Download</span>
            </a>
          )}
        </div>
      </form>

      <ReceiptPrivacyModal
        open={privacyOpen}
        title={privacyLinkLabel}
        content={privacyPolicy}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        onClose={() => setPrivacyOpen(false)}
      />

      <Toaster ref={toaster} />
    </div>
  );
}

export default compose(enhancer)(ReceiptEmailCapture);
